// A closed set of values that lives in the URL instead of in `useState`.
//
// Two things go wrong when a screen holds its tab in component state, and only
// the first is obvious.
//
// The obvious one is that the URL stops describing the page: a report someone
// wants to send a colleague is just `/reports`, and the back button leaves the
// screen entirely instead of returning to the previous tab.
//
// The second is the one this codebase actually got bitten by. A query gated on
// `tab === 'campaign'` cannot be warmed by a route loader, because at load time
// the tab does not exist yet — it is created by the component the loader runs
// before. So every tab switch is a cold fetch and an empty body, on exactly the
// screens where the data takes longest. Moving the gate into the URL is what
// makes those queries reachable from a loader at all.
//
// `parse` is total on purpose. A hand-edited or stale URL carrying `?tab=wat`
// resolves to the fallback rather than rendering a tab that does not exist, so
// a bad link degrades to the default view instead of a blank screen.

export interface SearchEnum<T extends string> {
  /** The query-string parameter name. */
  readonly key: string;
  readonly values: readonly T[];
  readonly fallback: T;
  /** Total: anything outside `values` — including absent — becomes `fallback`. */
  parse(search: Record<string, unknown>): T;
}

export function searchEnum<T extends string>(
  key: string,
  values: readonly T[],
  fallback: T,
): SearchEnum<T> {
  if (!values.includes(fallback)) {
    // A fallback outside its own value set would make `parse` return something
    // the screen has no branch for — the exact failure this is meant to prevent.
    throw new Error(`searchEnum(${key}): fallback ${fallback} is not one of its values`);
  }
  return {
    key,
    values,
    fallback,
    parse(search) {
      const raw = search[key];
      return values.includes(raw as T) ? (raw as T) : fallback;
    },
  };
}
